/* eslint-disable simple-import-sort/imports */

import arrow from 'assets/icons/arrow.svg';
import Container from 'components/Container';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';

import { dataInfoFaqs } from './data-InfoFaqs';
// eslint-disable-next-line import/named
import SectionTitle from 'components/SectionTitle/index.jsx';
import {
  InfoFaqsContent,
  InfoFaqsDesc,
  InfoFaqsIcon,
  InfoFaqsIconWrapper,
  InfoFaqsImage,
  InfoFaqsImageWrapper,
  InfoFaqsItem,
  InfoFaqsItemImage,
  InfoFaqsItemImageWrapper,
  InfoFaqsLeft,
  InfoFaqsProgressBar,
  InfoFaqsProgressBarFill,
  InfoFaqsRight,
  InfoFaqsText,
  InfoFaqsTitle,
  SectionInfoFaqs,
} from './styles';

const InfoFaqs = () => {
  const [selected, setSelected] = useState(0);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const sectionRef = useRef(null);
  const hasBeenVisibleRef = useRef(false);

  const startAutoSwitch = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setSelected((prev) => {
        const next = (prev + 1) % dataInfoFaqs.info.length;
        return next;
      });
    }, 10000);
  };

  const toggle = (i) => {
    setSelected(i);
    startAutoSwitch();
  };

  useEffect(() => {
    startTimeRef.current = Date.now();
    startAutoSwitch();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasBeenVisibleRef.current) {
            hasBeenVisibleRef.current = true;
            setSelected(0);
            startTimeRef.current = Date.now();
            startAutoSwitch();
          } else if (!entry.isIntersecting) {
            hasBeenVisibleRef.current = false;
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    setProgress(0);
    startTimeRef.current = Date.now();

    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const newProgress = Math.min((elapsed / 10000) * 100, 100);
      setProgress(newProgress);

      if (newProgress >= 100) {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
      }
    }, 50);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [selected]);

  const selectedItem = dataInfoFaqs.info[selected];

  return (
    <SectionInfoFaqs ref={sectionRef}>
      <Container className="info-faqs__container">
        <SectionTitle>{dataInfoFaqs.title}</SectionTitle>
        <InfoFaqsContent>
          <InfoFaqsLeft>
            {dataInfoFaqs.info.map(({ title, description, image }, index) => {
              const isSelected = selected === index;
              return (
                <InfoFaqsItem key={index}>
                  <InfoFaqsProgressBar>
                    <InfoFaqsProgressBarFill
                      $progress={isSelected ? progress : 0}
                    />
                  </InfoFaqsProgressBar>
                  <InfoFaqsTitle onClick={() => toggle(index)}>
                    <InfoFaqsText>{title}</InfoFaqsText>
                    <InfoFaqsIconWrapper $isOpen={isSelected}>
                      <InfoFaqsIcon src={arrow} />
                    </InfoFaqsIconWrapper>
                  </InfoFaqsTitle>
                  <InfoFaqsDesc $show={isSelected}>{description}</InfoFaqsDesc>
                  <InfoFaqsItemImageWrapper $show={isSelected}>
                    <InfoFaqsItemImage src={image} alt={title} />
                  </InfoFaqsItemImageWrapper>
                </InfoFaqsItem>
              );
            })}
          </InfoFaqsLeft>
          <InfoFaqsRight>
            <AnimatePresence mode="wait">
              <motion.div
                key={selected}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <InfoFaqsImageWrapper>
                  <InfoFaqsImage
                    src={selectedItem.image}
                    alt={selectedItem.title}
                  />
                </InfoFaqsImageWrapper>
              </motion.div>
            </AnimatePresence>
          </InfoFaqsRight>
        </InfoFaqsContent>
      </Container>
    </SectionInfoFaqs>
  );
};

export default InfoFaqs;
